import {Injectable} from '@angular/core';
import {from, Observable, of} from 'rxjs';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {concatMap, filter, first, map} from 'rxjs/operators';
import {findUniqueMatchWithId, readDocumentWithId} from '../common/firestore-utils';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {Tenant} from '../models/tenant.model';
import {DEFAULT_SCHOOL_ACCENT_COLOR, DEFAULT_SCHOOL_PRIMARY_COLOR} from '../common/ui-constants';
import {SubscriptionContent} from '../models/content/subscription-content.model';
import {DEFAULT_FAQS, DEFAULT_SUBSCRIPTION_BENEFITS} from './default.content';
import {HomePageContent} from '../models/content/home-page-content.model';
import {TenantSettings} from '../models/tenant-settings.model';
import {TenantService} from './tenant.service';
import {EmailProviderSettings} from '../models/email-provider-settings.model';
import firebase from 'firebase/compat/app';
import {HearAboutUsEvent} from '../authentication/auth-forms/hear-about-us/hear-about-us.event';
import {CookieService} from 'ngx-cookie-service';


@Injectable()
export class TenantsDBService {


  constructor(private afs: AngularFirestore,
              private afAuth: AngularFireAuth,
              private cookies: CookieService,
              private tenant: TenantService) {

  }

  /*
  *
  * If the tenant does not exist, create it, otherwise returns the existing tenant.
  *
  * */

  async createNewTenantWebsite(
    email: string,
    pictureUrl: string,
    displayName: string,
    createTestUser: boolean,
    subDomain: string,
    schoolName: string,
    referralSource: HearAboutUsEvent,
    allTenantsForEmail: Tenant[],
    userId:string,
    instructorStripeCustomerId:string) : Promise<Tenant> {

    console.log(`Creating new tenant from scratch for user ${userId}`);

    const trackingInfo = this.extractTrackingInfo();

    // an admin user can create multiple tenants for the same email. Each tenant is numbered.
    const tenantIdIndex = (allTenantsForEmail?.length ?? 0) + 1;

    const tenantId = `${userId}-${tenantIdIndex}`;

    console.log(`Creating new tenant with id ${tenantId}`);

    const newTenant :Partial<Tenant> = {
      id: tenantId,
      email,
      status: 'new',
      contactPageDisabled: true,
      allLessonsTabOff: true,
      subDomain,
      schoolName,
      accountCreatedAt: firebase.firestore.Timestamp.fromMillis(new Date().getTime()),
      supportEmail: email,
      guaranteeOffered: true,
      guaranteeDays: 30,
      brandTheme: {
        primaryColor: DEFAULT_SCHOOL_PRIMARY_COLOR,
        accentColor: DEFAULT_SCHOOL_ACCENT_COLOR,
        salesPageBannerColor: DEFAULT_SCHOOL_PRIMARY_COLOR
      },
      totalEmailsCollected: 0,
      instructorPricingPlan: 'growth',
      referralSource: (referralSource?.source ?? referralSource.other) ?? null,
      referralSourceOther: referralSource?.other ?? null,
      instructorStripeCustomerId,
      utms: trackingInfo?.utms ?? null,
      storageUsedInMb: 0,
    };

    const newSchoolUser: Partial<Tenant> = {
      id: userId,
      email,
      displayName
    };

    if (pictureUrl) {
      newSchoolUser.pictureUrl = pictureUrl;
    }

    if (createTestUser || email?.endsWith('onlinecoursehost.com') || document.location.host?.includes('onlinecoursehost-tests.com')) {
      newSchoolUser.testUser = true;
    }

    const batch = this.afs.firestore.batch();

    const schoolUserRef = this.afs.doc(`schools/${tenantId}/users/${userId}`).ref;
    batch.set(schoolUserRef, newSchoolUser);

    const tenantRef = this.afs.doc(`tenants/${tenantId}`).ref;
    batch.set(tenantRef, newTenant);

    // the tenant is also an admin in its own website
    const permissionsPath = `tenantSettings/${tenantId}/userPermissions/${userId}`

    console.log(`Creating permissions at ${permissionsPath}`);

    const permissionsRef = this.afs.doc(permissionsPath).ref;

    // this is not a security issue - only the tenant himself can do this write from the client
    batch.set(permissionsRef, {isAdmin: true, email, displayName});

    // set default tenant website content
    const defaultSubscriptionContent: SubscriptionContent = {
      subscriptionBenefits: DEFAULT_SUBSCRIPTION_BENEFITS,
      faqs: DEFAULT_FAQS
    };

    const subscriptionContentRef = this.afs.doc(`schools/${tenantId}/content/subscription`).ref;
    batch.set(subscriptionContentRef, defaultSubscriptionContent as any);

    const defaultHomePageContent: HomePageContent = {
      pageTitle: 'Click the Edit Header button below to edit this title and add a background',
      titleFontSize: 30,
      pageTitleColor: '#ffffff ',
      showViewCoursesButton: true,
      benefits: [
        {
          title: 'Hover with the mouse to edit this benefit',
          description: 'Enter the text benefit description here'
        },
        {
          title: 'Hover with the mouse to edit this benefit',
          description: 'Enter the text benefit description here'
        },
        {
          title: 'Hover with the mouse to edit this benefit',
          description: 'Enter the text benefit description here'
        }
      ]
    };

    const homePageContentRef = this.afs.doc(`schools/${tenantId}/content/home-page`).ref;
    batch.set(homePageContentRef, defaultHomePageContent as any);

    await batch.commit();

    return newTenant as Tenant;
  }

  findTenantById(tenantId: string) {
    return this.afs.doc<Tenant>(`tenants/${tenantId}`).valueChanges()
      .pipe(
        first()
      );
  }

  findTenantBySubdomain(subDomain: string): Observable<Tenant> {

    let query$ = this.afs
      .collection<Tenant>('tenants', ref => ref.where('subDomain', '==', subDomain).limit(1));

    return findUniqueMatchWithId(query$).pipe(first());
  }

  findTenantByCustomDomain(customDomain: string): Observable<Tenant> {
    let query$ = this.afs
      .collection<Tenant>('tenants', ref => ref.where('customDomain', '==', customDomain).limit(1));

    return findUniqueMatchWithId(query$).pipe(first());
  }

  saveTheme(tenantId: string, primaryColor: string, accentColor: string, salesPageBannerColor: string): Observable<any> {

    return from(this.afs.collection('tenants').doc(tenantId).update({brandTheme: {primaryColor, accentColor, salesPageBannerColor}}));

  }

  findCustomSubDomain(subDomain: string) {
    return this.afs.collection<Tenant>(`tenants`, ref => ref.where('subDomain', '==', subDomain)).valueChanges()
      .pipe(
        map(results => results.length == 1 ? results[0] : null),
        first()
      );
  }

  updateTenant(tenantId: string, changes: Partial<Tenant>): Observable<void> {

    const batch = this.afs.firestore.batch();

    const tenantRef = this.afs.doc(`tenants/${tenantId}`).ref;

    batch.update(tenantRef, changes);

    if (changes?.zapierConfig?.integrationToken && changes.zapierConfig.enabled) {

      const zapierIntegrationRef = this.afs.doc(`zapierIntegrationTokens/${changes.zapierConfig.integrationToken}`).ref;

      batch.set(zapierIntegrationRef, {
          tenantId: this.tenant.id
        },
        {merge: true});
    }

    // save pabbly integration token
    if (changes?.pabblyConfig?.integrationToken && changes.pabblyConfig.enabled) {

      const pabblyIntegrationRef = this.afs.doc(`pabblyIntegrationTokens/${changes.pabblyConfig.integrationToken}`).ref;

      batch.set(pabblyIntegrationRef, {
          tenantId: this.tenant.id
        },
        {merge: true});
    }

    return from(batch.commit());
  }

  loadTenantPrivateSettings(): Observable<TenantSettings> {
    return readDocumentWithId<TenantSettings>(this.afs.doc(`tenantSettings/${this.tenant.id}`)).pipe(first());
  }

  updateTenantSettings(tenantId: string, changes: Partial<TenantSettings>): Observable<void> {
    return from(this.afs.doc(`tenantSettings/${tenantId}`).update(changes));
  }

  activateEmailMarketingIntegration(emailProviderSettings: EmailProviderSettings): Observable<any> {

    console.log(`Called tenants db service activateEmailMarketingIntegration()`);

    const batch = this.afs.firestore.batch();

    const tenantSettingsRef = this.afs.doc(`tenantSettings/${this.tenant.id}`).ref,
      tenantRef = this.afs.doc(`tenants/${this.tenant.id}`).ref;

    batch.set(tenantSettingsRef, {emailProvider: emailProviderSettings}, {merge: true});

    batch.update(tenantRef, {isNewsletterIntegrationActive: true});

    return from(batch.commit());

  }

  cancelEmailMarketingIntegration() {

    const batch = this.afs.firestore.batch();

    const tenantSettingsRef = this.afs.doc(`tenantSettings/${this.tenant.id}`).ref,
      tenantRef = this.afs.doc(`tenants/${this.tenant.id}`).ref;

    batch.update(tenantSettingsRef, {emailProvider: <any>firebase.firestore.FieldValue.delete()});

    batch.update(tenantRef, {isNewsletterIntegrationActive: false});

    return from(batch.commit());
  }

  /**
   *
   * returns all the tenants matching a single email. The same email could be used to manage the accounts of several tenants.
   *
   */

  async findAllTenantsByEmail(email: string) {

    const querySnapshot = await this.afs.collection<Tenant>('tenants', ref => ref.where('email', '==', email)).get().toPromise();

    if (querySnapshot.empty) {
      return [];
    }

    const allTenantsForEmail = querySnapshot.docs.map(doc => {
      return {
        id: doc.id,
        ...doc.data()
      };
    });

    return allTenantsForEmail;
  }

  extractTrackingInfo() {

    const payload = this.cookies.get("OCH_SOURCES");

    if (payload) {

      try {

        const ochSources = JSON.parse(payload);

        console.log("OCH_SOURCES cookie parsed", ochSources);

        return ochSources;
      }
      catch (error) {
        console.error("Error parsing OCH_SOURCES cookie", error);
        return null;
      }
    }
    }
}








